import { lazy, Suspense } from 'react';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { X } from 'lucide-react';
import { ProfilePicker } from './profile-picker';
import { Button } from '@/components/ui/button';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { getValhallaUrl, VALHALLA_CLIENT_HEADERS } from '@/utils/valhalla';
import { useCommonStore, type Profile } from '@/stores/common-store';
import { useDirectionsQuery } from '@/hooks/use-directions-queries';
import { useIsochronesQuery } from '@/hooks/use-isochrones-queries';
import { useNavigate, useParams } from '@tanstack/react-router';

const DirectionsControl = lazy(() =>
  import('./directions/directions').then((module) => ({
    default: module.DirectionsControl,
  }))
);

const IsochronesControl = lazy(() =>
  import('./isochrones/isochrones').then((module) => ({
    default: module.IsochronesControl,
  }))
);

const TilesControl = lazy(() =>
  import('./tiles/tiles').then((module) => ({ default: module.TilesControl }))
);

const PanelFallback = ({ label }: { label: string }) => (
  <p role="status" className="text-sm text-muted-foreground">
    Loading {label}…
  </p>
);

export const RoutePlanner = () => {
  const { activeTab } = useParams({ from: '/$activeTab' });
  const navigate = useNavigate({ from: '/$activeTab' });
  const directionsPanelOpen = useCommonStore(
    (state) => state.directionsPanelOpen
  );
  const loading = useCommonStore((state) => state.loading);
  const toggleDirections = useCommonStore((state) => state.toggleDirections);
  const { refetch: refetchDirections } = useDirectionsQuery();
  const { refetch: refetchIsochrones } = useIsochronesQuery();

  const {
    data: lastUpdate,
    isLoading: isLoadingLastUpdate,
    isError: isErrorLastUpdate,
  } = useQuery({
    queryKey: ['lastUpdate'],
    queryFn: async () => {
      const response = await fetch(`${getValhallaUrl()}/status`, {
        headers: VALHALLA_CLIENT_HEADERS,
      });
      if (!response.ok) throw new Error('Routing status is unavailable');
      const data = (await response.json()) as {
        tileset_last_modified?: number | string;
      };
      const raw = data.tileset_last_modified;
      const date =
        typeof raw === 'number'
          ? new Date(raw * 1000)
          : new Date(raw ?? Number.NaN);
      if (Number.isNaN(date.getTime())) {
        throw new Error('Routing status returned an invalid graph date');
      }
      return date;
    },
    staleTime: 1000 * 60 * 60,
    refetchOnWindowFocus: false,
  });

  const handleTabChange = (value: string) => {
    navigate({ params: { activeTab: value } });
  };

  const handleProfileChange = (value: Profile) => {
    navigate({
      search: (previous) => ({ ...previous, profile: value }),
      replace: true,
    });

    if (activeTab === 'isochrones') {
      refetchIsochrones();
      window.setTimeout(() => refetchDirections(), 1000);
    } else {
      refetchDirections();
      window.setTimeout(() => refetchIsochrones(), 1000);
    }
  };

  return (
    <Sheet open={directionsPanelOpen} modal={false}>
      <SheetContent
        side="left"
        className="max-h-screen w-full max-w-full gap-0 overflow-hidden sm:w-[400px] sm:max-w-[400px]"
      >
        <Tabs
          value={activeTab}
          className="flex min-h-0 flex-1 flex-col"
          onValueChange={handleTabChange}
        >
          <SheetHeader className="items-start justify-between border-b px-4 pb-3 pt-4">
            <div className="min-w-0">
              <p className="font-mono text-[11px] uppercase tracking-[0.14em] text-muted-foreground">
                maps.prawel.pl
              </p>
              <SheetTitle className="text-2xl tracking-tight">
                Route planner
              </SheetTitle>
              <SheetDescription>
                Workers-native routing, reachability and graph inspection.
              </SheetDescription>
            </div>
            <Button
              variant="ghost"
              size="icon"
              className="min-h-11 min-w-11 shrink-0"
              onClick={toggleDirections}
              data-testid="close-directions-button"
              aria-label="Close route planner"
            >
              <X className="size-4" />
            </Button>
          </SheetHeader>

          <div className="border-b px-4 py-3">
            <TabsList
              className="grid h-[50px] w-full grid-cols-3"
              aria-label="Routing tools"
            >
              <TabsTrigger
                value="directions"
                className="h-11"
                data-testid="directions-tab-button"
              >
                Directions
              </TabsTrigger>
              <TabsTrigger
                value="isochrones"
                className="h-11"
                data-testid="isochrones-tab-button"
              >
                Reach
              </TabsTrigger>
              <TabsTrigger
                value="tiles"
                className="h-11"
                data-testid="tiles-tab-button"
              >
                Graph
              </TabsTrigger>
            </TabsList>
          </div>

          {activeTab !== 'tiles' && (
            <div className="border-b px-4 py-3">
              <ProfilePicker
                loading={loading}
                onProfileChange={handleProfileChange}
              />
            </div>
          )}

          <div className="min-h-0 flex-1 overflow-y-auto">
            <TabsContent
              value="directions"
              className="flex flex-col gap-3 px-4 py-3"
            >
              <Suspense fallback={<PanelFallback label="directions" />}>
                <DirectionsControl />
              </Suspense>
            </TabsContent>
            <TabsContent
              value="isochrones"
              className="flex flex-col gap-3 px-4 py-3"
            >
              <Suspense fallback={<PanelFallback label="reachability" />}>
                <IsochronesControl />
              </Suspense>
            </TabsContent>
            <TabsContent
              value="tiles"
              className="flex min-h-full flex-col gap-3 px-4 py-3"
            >
              <Suspense fallback={<PanelFallback label="graph inspector" />}>
                <TilesControl />
              </Suspense>
            </TabsContent>
          </div>

          {activeTab !== 'tiles' && (
            <div
              role="status"
              className="flex min-h-10 items-center border-t px-4 py-2 text-xs text-muted-foreground"
            >
              {isLoadingLastUpdate && <span>Checking routing graph…</span>}
              {isErrorLastUpdate && (
                <span role="alert" className="text-destructive">
                  Routing graph unavailable
                </span>
              )}
              {lastUpdate && (
                <span>
                  Graph build: {format(lastUpdate, 'yyyy-MM-dd, HH:mm')}
                </span>
              )}
            </div>
          )}
        </Tabs>
      </SheetContent>
    </Sheet>
  );
};
